import { Controller, FieldPath, FieldValues, useFormContext } from 'react-hook-form';
import { DateRangeFormFieldValue } from './date-range-form-field-value';
import { DateRangePresetId, DateRangeValue } from './date-range-presets';
import { DateRangeSelector } from './DateRangeSelector';

export type DateRangeSelectorFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
  /** Earliest allowed range start; same semantics as `DateRangeSelector` `minDate` (absolute instant). */
  minDate?: Date;
  /** Latest allowed day in custom range / single-date pickers; capped to “now” like `DateRangeSelector`. */
  maxDate?: Date;
};

/**
 * Controlled `DateRangeSelector` bound to React Hook Form (`from` / `to` as ISO strings + `presetId`).
 */
export function DateRangeSelectorField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ name, minDate, maxDate }: DateRangeSelectorFieldProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const v = field.value as DateRangeFormFieldValue;
        const value: DateRangeValue | null =
          v.from && v.to
            ? {
                from: new Date(v.from),
                to: new Date(v.to),
              }
            : null;
        const presetId = v.presetId as DateRangePresetId;

        const handleChange = (range: DateRangeValue, newPresetId: DateRangePresetId) => {
          field.onChange({
            from: range.from.toISOString(),
            to: range.to.toISOString(),
            presetId: newPresetId,
          });
        };

        return (
          <DateRangeSelector
            value={value}
            presetId={presetId}
            onChange={handleChange}
            minDate={minDate}
            maxDate={maxDate}
            buttonProps={{
              variant: 'outlined',
              color: 'secondary',
              sx: { width: '100%', backgroundColor: 'background.paper', minHeight: 40 },
            }}
          />
        );
      }}
    />
  );
}
